import { Route } from 'react-router-dom';
import {
    AlarmClock,
    BadgeCheck,
    Building2,
    CalendarCheck,
    CalendarDays,
    ChartColumn,
    Contact,
    CreditCard,
    Hourglass,
    Layers,
    Megaphone,
    MessageCircle,
    Package,
    QrCode,
    ReceiptText,
    Repeat,
    Scissors,
    ShoppingBag,
    Store,
    UserCog,
    UserRoundCheck,
    UsersRound,
    Wallet,
    type LucideIcon,
} from 'lucide-react';
import Agenda from './modules/agenda/estabelecimento/Agenda';
import Content from './dashboard/content/Content';
import PageNotFound from './components/PageNotFound';
import Servicos from './modules/servicos/Servicos';
import Estabelecimento from './modules/configuracoes/estabelecimento/Estabelecimento';
import Clientes from './modules/clientes/Clientes';
import Usuarios from './modules/usuarios/Usuarios';
import ContentActions from './dashboard/content/Actions';
import ConfiguracoesConta from './modules/configuracoes/usuario/Configuracoes';
import UsuariosSistema from './modules/usuarios-sistema/UsuariosSistema';
import { UsersProvider } from './modules/usuarios-sistema/Context';
import { ServicosProvider } from './modules/servicos/Context';
import NotificationsAll from './notifications/NotificationsAll';
import Atendentes from './modules/atendentes/Atendentes';
import { AtendentesProvider } from './modules/atendentes/Context';
import AgendaCliente from './modules/agenda/cliente/Agenda';
import { AgendaClienteProvider } from './modules/agenda/cliente/Context';
import EstabelecimentosClient from './modules/estabelecimentos/client/Estabelecimentos';
import { EstabsClientProvider } from './modules/estabelecimentos/client/Context';
import Planos from './modules/planos/estabelecimento/Planos';
import { PlanosProvider } from './modules/planos/estabelecimento/Context';
import PlanosCliente from './modules/planos/cliente/Planos';
import { EstabsProvider } from './modules/estabelecimentos/system-admin/Context';
import Estabelecimentos from './modules/estabelecimentos/system-admin/Estabelecimentos';
import { ActionsClients } from './modules/clientes/Actions';
import { ClientsProvider } from './modules/clientes/Context';
import { Dashboards } from './modules/Dashboard/Dashboards';
import { DashboardProvider } from './modules/Dashboard/Context';
import Recorrencias from './modules/recorrencias/Recorrencias';
import DetalhesRecorrencia from './modules/recorrencias/Detalhes';
import { RecorrenciasProvider } from './modules/recorrencias/Context';
import GruposClientesRoute from './modules/grupos-clientes/GruposClientes';
import { FilterAgenda } from './modules/agenda/estabelecimento/components/AgendaFilter';
import Assinaturas from './modules/assinaturas/estabelecimento/Assinaturas';
import { AssinaturasProvider } from './modules/assinaturas/estabelecimento/Context';
import Estoque from './modules/estoque/Estoque';
import { EstoqueProvider } from './modules/estoque/Context';
import EstoqueSubMenu from './modules/estoque/components/SubMenu';
import FilaEspera from './modules/fila-espera/estabelecimento/FilaEspera';
import { FilaEsperaProvider } from './modules/fila-espera/estabelecimento/Context';
import Vendas from './modules/vendas/Vendas';
import { VendasProvider } from './modules/vendas/Context';
import Financeiro from './modules/financeiro/Financeiro';
import OfertasHorario from './modules/ofertas-horario/OfertasHorario';
import WhatsappInbox from './modules/whatsapp/Whatsapp';
import BillingOverview from './modules/billing/system-admin/BillingOverview';
import BillingSettings from './modules/billing/estabelecimento/BillingSettings';
import PagamentosConfiguracao from './modules/configuracoes/pagamentos/Pagamentos';
import Lembretes from './modules/lembretes/Lembretes';
import type { SalonFlowFeatureKey } from './plans/salonflowPlans';
import { SALONFLOW_FEATURES } from './plans/salonflowPlans';

type Menu = {
    title: string;
    path: string;
    types: string[];
    icon: LucideIcon;
    subMenu?: any;
    planFeature?: SalonFlowFeatureKey;
};

type UserRoute = {
    path: string;
    title: string;
    types: string[];
    planFeature?: SalonFlowFeatureKey;
    Element: any;
    Actions?: any;
    Provider?: any;
    returnHome?: boolean;
    returnTo?: string;
    actionTitles?: Record<string, string>;
    actionParents?: Record<string, string>;
    hideHeader?: boolean;
    // false: cabecalho rola com a pagina (ex.: Agenda, onde a barra do dia e que fica fixa).
    stickyHeader?: boolean;
};

const menus = (user: any) => {
    let menus: Menu[] = [
        {
            title: 'Dashboard',
            path: '/dashboard',
            types: ['root', 'admin', 'worker'],
            icon: ChartColumn,
            subMenu: false,
            planFeature: SALONFLOW_FEATURES.DASHBOARD_BASIC,
        },
        {
            title: 'Serviços',
            path: '/servicos',
            types: ['root', 'admin'],
            icon: Scissors,
            planFeature: SALONFLOW_FEATURES.SERVICES,
        },
        {
            title: 'Estoque',
            path: '/estoque',
            types: ['root', 'admin', 'worker'],
            icon: Package,
            subMenu: <EstoqueSubMenu />,
            planFeature: SALONFLOW_FEATURES.STOCK,
        },
        {
            title: 'Vendas',
            path: '/vendas',
            types: ['root', 'admin', 'worker'],
            icon: ShoppingBag,
            planFeature: SALONFLOW_FEATURES.SALES,
        },
        {
            title: 'Financeiro',
            path: '/financeiro',
            types: ['root', 'admin'],
            icon: Wallet,
            planFeature: SALONFLOW_FEATURES.FINANCE,
        },
        {
            title: 'Pagamentos',
            path: '/configuracoes/pagamentos',
            types: ['root', 'admin'],
            icon: QrCode,
        },
        {
            title: 'Recorrências',
            path: '/recorrencias',
            types: ['root', 'admin', 'worker'],
            icon: Repeat,
            planFeature: SALONFLOW_FEATURES.RECURRENCES,
        },
        {
            title: 'Fila de Espera',
            path: '/fila-espera',
            types: ['root', 'admin', 'worker'],
            icon: Hourglass,
            planFeature: SALONFLOW_FEATURES.WAITING_LIST,
        },
        {
            title: 'Ofertas de Horário',
            path: '/ofertas-horario',
            types: ['root', 'admin', 'worker'],
            icon: Megaphone,
            planFeature: SALONFLOW_FEATURES.TIME_OFFERS,
        },
        {
            title: 'Lembretes',
            path: '/lembretes',
            types: ['root', 'admin', 'worker'],
            icon: AlarmClock,
            planFeature: SALONFLOW_FEATURES.REMINDERS,
        },
        {
            title: 'WhatsApp',
            path: '/whatsapp',
            types: ['root', 'admin', 'worker'],
            icon: MessageCircle,
            planFeature: SALONFLOW_FEATURES.WHATSAPP_OPERATIONAL,
        },
        {
            title: 'Clientes',
            path: '/clientes',
            types: ['root', 'admin', 'worker'],
            icon: Contact,
            planFeature: SALONFLOW_FEATURES.CLIENTS,
        },
        {
            title: 'Grupos de Clientes',
            path: '/grupos-clientes',
            types: ['root', 'admin'],
            icon: UsersRound,
            planFeature: SALONFLOW_FEATURES.CLIENT_GROUPS,
        },
        {
            title: 'Planos',
            path: '/planos',
            types: ['root', 'admin', 'worker', 'client'],
            icon: Layers,
            planFeature: SALONFLOW_FEATURES.PLANS,
        },
        // 🔹 NOVO MENU: ASSINATURAS
        {
            title: 'Assinaturas',
            path: '/assinaturas',
            types: ['root', 'admin', 'worker'],
            icon: BadgeCheck,
            planFeature: SALONFLOW_FEATURES.SUBSCRIPTIONS,
        },
        {
            title: 'Atendentes',
            path: '/atendentes',
            types: ['root', 'admin'],
            icon: UserRoundCheck,
            planFeature: SALONFLOW_FEATURES.ATTENDANTS,
        },
        {
            title: 'Meu Plano',
            path: '/meu-plano',
            types: ['root', 'admin'],
            icon: CreditCard,
        },
        {
            title: 'Estabelecimentos',
            path: '/estabelecimentos',
            types: ['root'],
            icon: Building2,
        },
        {
            title: 'Cobranças SaaS',
            path: '/cobrancas-saas',
            types: ['root'],
            icon: ReceiptText,
        },
        {
            title: 'Usuários Sistema',
            path: '/usuario-sistema',
            types: ['root'],
            icon: UserCog,
        },
        {
            title: 'Meus estabelecimentos',
            path: '/meus-estabelecimentos',
            types: ['client'],
            icon: Store,
        },
    ];

    if (user.client && (user.root || user.worker || user.admin)) {
        menus = [
            {
                title: 'Agenda',
                path: '/',
                types: ['root', 'admin', 'worker'],
                icon: CalendarDays,
                subMenu: user.admin || user.worker || user.root ? <FilterAgenda /> : false,
                planFeature: SALONFLOW_FEATURES.AGENDA,
            },
            {
                title: 'Meus agendamentos',
                path: '/minha-agenda',
                types: ['client'],
                icon: CalendarCheck,
            },
            ...menus,
        ];
    } else {
        menus = [
            {
                title: 'Agenda',
                path: '/',
                types: ['root', 'admin', 'worker', 'client'],
                icon: CalendarDays,
                subMenu: user.admin || user.worker || user.root ? <FilterAgenda /> : false,
                planFeature: SALONFLOW_FEATURES.AGENDA,
            },
            ...menus,
        ];
    }

    return menus.filter((e) => e?.types?.some((type) => !!user?.[type]));
};

// Titulos exibidos no cabecalho quando a rota esta em um fluxo dedicado (?action=...).
const AGENDA_ACTION_TITLES = { add: 'Novo atendimento' };

const userRoutes = (user: any) => {
    let routes: UserRoute[] = [
        {
            path: '/servicos',
            title: 'Serviços',
            types: ['root', 'admin'],
            planFeature: SALONFLOW_FEATURES.SERVICES,
            Element: () => (
                <ServicosProvider>
                    <Servicos />
                </ServicosProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'order' }, { type: 'add' }]} />,
            actionTitles: { add: 'Novo serviço', edit: 'Editar serviço' },
        },
        {
            path: '/estoque',
            title: 'Estoque',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.STOCK,
            Element: () => (
                <EstoqueProvider>
                    <Estoque />
                </EstoqueProvider>
            ),
            Actions: () => <></>,
            actionTitles: {
                items: 'Itens',
                detail: 'Item',
                movement: 'Movimentação',
                recipes: 'Receitas',
                reports: 'Relatórios',
            },
            actionParents: { detail: 'items' },
        },
        {
            path: '/vendas',
            title: 'Vendas',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.SALES,
            Element: () => (
                <VendasProvider>
                    <Vendas />
                </VendasProvider>
            ),
            Actions: () => <></>,
            actionTitles: {
                add: 'Nova venda',
                detail: 'Venda',
                recorrentes: 'Recorrentes',
                'add-recorrente': 'Nova recorrência',
                recorrente: 'Recorrência',
            },
            actionParents: { 'add-recorrente': 'recorrentes', recorrente: 'recorrentes' },
        },
        {
            path: '/financeiro',
            title: 'Financeiro',
            types: ['root', 'admin'],
            planFeature: SALONFLOW_FEATURES.FINANCE,
            Element: () => <Financeiro />,
            Actions: () => <></>,
        },
        {
            path: '/recorrencias',
            title: 'Recorrências',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.RECURRENCES,
            Element: () => (
                <RecorrenciasProvider>
                    <Recorrencias />
                </RecorrenciasProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
        },
        {
            path: '/fila-espera',
            title: 'Fila de Espera',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.WAITING_LIST,
            Element: () => (
                <FilaEsperaProvider>
                    <FilaEspera />
                </FilaEsperaProvider>
            ),
            Actions: () => <ContentActions actions={[]} />,
        },
        {
            path: '/ofertas-horario',
            title: 'Ofertas de Horário',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.TIME_OFFERS,
            Element: () => <OfertasHorario />,
            Actions: () => <></>,
            actionTitles: { detalhe: 'Oferta' },
        },
        {
            path: '/lembretes',
            title: 'Lembretes',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.REMINDERS,
            Element: () => <Lembretes />,
            actionTitles: { add: 'Novo lembrete', edit: 'Editar lembrete' },
            Actions: () => (user?.root || user?.admin ? <ContentActions actions={[{ type: 'add' }]} /> : <></>),
        },
        {
            path: '/whatsapp',
            title: 'WhatsApp',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.WHATSAPP_OPERATIONAL,
            Element: () => <WhatsappInbox />,
            Actions: () => <></>,
            hideHeader: true,
        },
        {
            path: '/recorrencias/:id',
            title: 'Detalhes da Recorrência',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.RECURRENCES,
            Element: () => <DetalhesRecorrencia />,
            returnHome: true,
            returnTo: '/recorrencias',
        },
        {
            path: '/atendentes',
            title: 'Atendentes',
            types: ['root', 'admin'],
            planFeature: SALONFLOW_FEATURES.ATTENDANTS,
            Element: () => (
                <AtendentesProvider>
                    <Atendentes />
                </AtendentesProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
            actionTitles: { add: 'Novo atendente', edit: 'Editar atendente', 'agenda-settings': 'Agenda do atendente' },
        },
        {
            path: '/dashboard',
            title: 'Dashboard',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.DASHBOARD_BASIC,
            Element: () => (
                <DashboardProvider>
                    <Dashboards />
                </DashboardProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'print' }]} />,
        },
        {
            path: '/clientes',
            title: 'Clientes',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.CLIENTS,
            Provider: ClientsProvider,
            Element: () => <Clientes />,
            Actions: () => <ActionsClients />,
            actionTitles: { mensagem: 'Enviar mensagem', 'mensagem-push-all': 'Mensagem para todos' },
        },
        {
            path: '/grupos-clientes',
            title: 'Grupos de Clientes',
            types: ['root', 'admin'],
            planFeature: SALONFLOW_FEATURES.CLIENT_GROUPS,
            Element: () => <GruposClientesRoute />,
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
            actionTitles: { add: 'Novo grupo', edit: 'Editar grupo' },
        },
        {
            path: '/estabelecimentos',
            title: 'Estabelecimentos',
            types: ['root'],
            Element: () => (
                <EstabsProvider>
                    <Estabelecimentos />
                </EstabsProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
        },
        {
            path: '/cobrancas-saas',
            title: 'Cobranças SaaS',
            types: ['root'],
            Element: () => <BillingOverview />,
            Actions: () => <></>,
        },
        {
            path: '/meu-plano',
            title: 'Meu Plano',
            types: ['root', 'admin'],
            Element: () => <BillingSettings />,
            Actions: () => <></>,
        },
        {
            path: '/meus-estabelecimentos',
            title: 'Meus Estabelecimentos',
            types: ['client'],
            Element: () => (
                <EstabsClientProvider>
                    <EstabelecimentosClient />
                </EstabsClientProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
            actionTitles: { add: 'Adicionar estabelecimento' },
        },
        {
            path: '/planos',
            title: 'Planos',
            types: ['root', 'admin'],
            planFeature: SALONFLOW_FEATURES.PLANS,
            Element: () => (
                <PlanosProvider>
                    <Planos />
                </PlanosProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
            actionTitles: { add: 'Novo plano', edit: 'Editar plano' },
        },
        {
            path: '/planos',
            title: 'Planos',
            types: ['client'],
            planFeature: SALONFLOW_FEATURES.PLANS,
            Element: () => <PlanosCliente />,
            Actions: () => <></>,
        },
        // 🔹 NOVA ROTA: ASSINATURAS
        {
            path: '/assinaturas',
            title: 'Assinaturas',
            types: ['root', 'admin', 'worker'],
            planFeature: SALONFLOW_FEATURES.SUBSCRIPTIONS,
            Element: () => (
                <AssinaturasProvider>
                    <Assinaturas />
                </AssinaturasProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
            actionTitles: { add: 'Nova assinatura', periodos: 'Assinatura', recorrencias: 'Assinatura' },
        },
        {
            path: '/usuarios',
            title: 'Usuários',
            types: ['root'],
            Element: Usuarios,
        },
        {
            path: '/usuario-sistema',
            title: 'Usuários sistema',
            types: ['root'],
            Element: () => (
                <UsersProvider>
                    <UsuariosSistema />
                </UsersProvider>
            ),
            Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
        },
        {
            path: '/configuracoes/usuario',
            title: 'Configurações da Conta',
            types: ['root', 'admin', 'worker', 'client'],
            Element: ConfiguracoesConta,
            returnHome: true,
        },
        {
            path: '/configuracoes/estabelecimento',
            title: 'Estabelecimento',
            types: ['root', 'admin'],
            Element: Estabelecimento,
            returnHome: true,
        },
        {
            path: '/configuracoes/pagamentos',
            title: 'Pagamentos',
            types: ['root', 'admin'],
            Element: PagamentosConfiguracao,
            returnHome: true,
        },
        {
            path: '/notificacoes',
            title: 'Notificações',
            types: ['root', 'admin', 'worker', 'client'],
            planFeature: SALONFLOW_FEATURES.NOTIFICATIONS,
            Element: NotificationsAll,
            returnHome: true,
        },
    ];

    if (user.client && (user.root || user.worker || user.admin)) {
        routes = [
            {
                path: '/',
                title: 'Agenda',
                actionTitles: AGENDA_ACTION_TITLES,
                stickyHeader: false,
                types: ['root', 'admin', 'worker'],
                planFeature: SALONFLOW_FEATURES.AGENDA,
                Element: () => <Agenda />,
                Actions: () => <ContentActions actions={[{ type: 'hiddeValues' }, { type: 'add' }, { type: 'settings' }]} />,
            },
            {
                path: '/agenda',
                title: 'Agenda',
                actionTitles: AGENDA_ACTION_TITLES,
                stickyHeader: false,
                types: ['root', 'admin', 'worker'],
                planFeature: SALONFLOW_FEATURES.AGENDA,
                Element: () => <Agenda />,
                Actions: () => <ContentActions actions={[{ type: 'hiddeValues' }, { type: 'add' }, { type: 'settings' }]} />,
            },
            {
                path: '/minha-agenda',
                title: 'Meus agendamentos',
                types: ['client'],
                Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
                Element: () => (
                    <AgendaClienteProvider>
                        <AgendaCliente />
                    </AgendaClienteProvider>
                ),
            },
            ...routes,
        ];
    } else {
        routes = [
            {
                path: '/',
                title: 'Agenda',
                actionTitles: AGENDA_ACTION_TITLES,
                stickyHeader: false,
                types: ['root', 'admin', 'worker'],
                planFeature: SALONFLOW_FEATURES.AGENDA,
                Element: () => <Agenda />,
                Actions: () => <ContentActions actions={[{ type: 'hiddeValues' }, { type: 'add' }, { type: 'settings' }]} />,
            },
            {
                path: '/agenda',
                title: 'Agenda',
                actionTitles: AGENDA_ACTION_TITLES,
                stickyHeader: false,
                types: ['root', 'admin', 'worker'],
                planFeature: SALONFLOW_FEATURES.AGENDA,
                Element: () => <Agenda />,
                Actions: () => <ContentActions actions={[{ type: 'hiddeValues' }, { type: 'add' }, { type: 'settings' }]} />,
            },
            {
                path: '/',
                title: 'Agenda',
                actionTitles: AGENDA_ACTION_TITLES,
                stickyHeader: false,
                types: ['client'],
                Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
                Element: () => (
                    <AgendaClienteProvider>
                        <AgendaCliente />
                    </AgendaClienteProvider>
                ),
            },
            {
                path: '/agenda',
                title: 'Agenda',
                actionTitles: AGENDA_ACTION_TITLES,
                stickyHeader: false,
                types: ['client'],
                Actions: () => <ContentActions actions={[{ type: 'add' }]} />,
                Element: () => (
                    <AgendaClienteProvider>
                        <AgendaCliente />
                    </AgendaClienteProvider>
                ),
            },
            ...routes,
        ];
    }

    return [
        ...routes
            .filter((e) => e?.types?.some((type) => !!user?.[type]))
            .map(
                (
                    { path, Element, title, Actions, returnHome, Provider, returnTo, planFeature, hideHeader, actionTitles, actionParents, stickyHeader }: any,
                    i,
                ) => (
                    <Route
                        key={i}
                        path={path}
                        element={
                            <Content
                                title={title}
                                Element={Element}
                                Actions={Actions}
                                returnHome={returnHome}
                                Provider={Provider}
                                returnTo={returnTo}
                                planFeature={planFeature}
                                hideHeader={hideHeader}
                                actionTitles={actionTitles}
                                actionParents={actionParents}
                                stickyHeader={stickyHeader}
                            />
                        }
                    />
                ),
            ),
        <Route key="not-found" path="*" element={<PageNotFound />} />,
    ];
};

function filterMenusWithPlan(
    user: any,
    menuShowEntry: (planFeature?: SalonFlowFeatureKey) => boolean,
) {
    return menus(user).filter((entry) => menuShowEntry(entry.planFeature));
}

export { menus, userRoutes, filterMenusWithPlan };
